"use client"

import React, { useEffect, useRef, useState } from "react"
import { classNames } from "@punks/ui-core"

export type AnimationType =
  | "fadeIn"
  | "fadeInUp"
  | "fadeInDown"
  | "fadeInLeft"
  | "fadeInRight"
  | "slideInUp"
  | "slideInDown"
  | "slideInLeft"
  | "slideInRight"

export interface AnimateOnEnterProps {
  children: React.ReactNode
  delay?: number
  animationType?: AnimationType
  className?: string
  disabled?: boolean
  duration?: number
}

const getAnimationClasses = (
  animationType: AnimationType,
  isVisible: boolean,
  duration: number = 1000,
  delay: number = 0
): string => {
  // Create a custom transition class based on the duration
  // Base duration class is 1000 = 1s, but we'll make sure it can go up to 1500ms
  const durationValue = Math.min(Math.max(Math.round(duration / 100) * 100, 100), 1500)
  const delayValue = delay > 0 ? Math.min(Math.round(delay / 100) * 100, 1000) : 0
  
  // Base transition class with configurable duration and delay
  // Using cubic-bezier for a smoother, more natural easing
  const transitionClass = `transition-all ${
    durationValue === 1000 ? 'duration-1000' : `duration-${durationValue}`
  } ${delayValue > 0 ? `delay-${delayValue}` : ''} ease-[cubic-bezier(0.16,1,0.3,1)]`
  
  // Initial hidden state classes with more pronounced animations
  const hiddenClasses = {
    fadeIn: "opacity-0",
    fadeInUp: "opacity-0 translate-y-12", // More movement
    fadeInDown: "opacity-0 -translate-y-12", // More movement
    fadeInLeft: "opacity-0 -translate-x-12", // More movement
    fadeInRight: "opacity-0 translate-x-12", // More movement
    slideInUp: "opacity-0 translate-y-24", // More dramatic slide
    slideInDown: "opacity-0 -translate-y-24", // More dramatic slide
    slideInLeft: "opacity-0 -translate-x-24", // More dramatic slide
    slideInRight: "opacity-0 translate-x-24" // More dramatic slide
  }

  // Visible state classes (all animations end with opacity-100 and no translation)
  const visibleClass = "opacity-100 translate-x-0 translate-y-0"

  return classNames(
    transitionClass,
    isVisible ? visibleClass : hiddenClasses[animationType] || hiddenClasses.fadeIn
  )
}

const getOverflowClass = (animationType?: AnimationType): string => {
  switch (animationType) {
    case "slideInDown":
    case "slideInUp":
    case "slideInLeft":
    case "slideInRight":
    default:
      return "overflow-hidden"
  }
}

const AnimateOnEnter = ({
  children,
  animationType = "fadeInUp",
  delay = 0,
  duration = 1000, // Longer default duration (1 second)
  className,
  disabled = false,
}: AnimateOnEnterProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (disabled) return
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1, // Trigger when at least 10% of the element is visible
      }
    )

    if (elementRef.current) {
      observer.observe(elementRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [disabled])

  if (disabled) {
    return <div className={className}>{children}</div>
  }

  const animationClasses = getAnimationClasses(
    animationType,
    isVisible,
    duration,
    delay
  )

  return (
    <div 
      ref={elementRef} 
      className={classNames(getOverflowClass(animationType), className)}
    >
      <div className={animationClasses}>
        {children}
      </div>
    </div>
  )
}

export default AnimateOnEnter